"use client";
//css
import styles from "./index.module.scss";
import {
  queryFeedbackConfig,
  questionFeedback,
  FeedbackPropsType,
} from "@/app/api/robot/chatContent";
// view
import { Button, Form, Radio, Space, Spin } from "@douyinfe/semi-ui";
import { useState, useEffect } from "react";
import {
  ReportBizTypeEnum,
  ReportEventEnum,
  ReportSceneTypeEnum,
  ReportParentContentEnum,
} from "@/app/types/typeConfig/report";
import {
  MessageType,
  MessageSender,
  HeaderType,
  RobotMessageType,
} from "@/app/store/types";
import {
  IMConfigTypeEnum,
  OriginType,
  FeedbackTypeEnum,
} from "@/app/types/typeConfig/chatQuestion";
// ts
import { useTranslations, useCurrentLocale } from "@/app/lib/LocaleProvider";
import useReportRequestQueue from "@/app/hooks/robot/useReportRequestQueue";
import { AIAnswerKnowledgeId } from "@/const/issueType";
import { isApp } from "@/app/bridge";
import { useCurrentUser } from "@/app/lib/AuthProvider";
import { createSessionId, createClientUUid } from "@/app/utils/string";
import { useMessageStore } from "@/app/store";

type FeedbackProps = {
  templateProps?: {
    likeOrDislike: "like" | "dislike";
    requestId?: string;
  };
  parentContentInfo?: {
    parentContentId: string;
    parentContent: string;
    parentContentType: ReportParentContentEnum;
  };
  /** 提交反馈后获取回复模板的配置类型，默认为 techAiFAQ */
  configType?: IMConfigTypeEnum;
};

export default function Feedback({
  templateProps,
  parentContentInfo,
  configType = IMConfigTypeEnum.techAiFAQ,
}: FeedbackProps) {
  const t = useTranslations();
  const _lng = useCurrentLocale();
  const user = useCurrentUser();
  const { setRobotMessage } = useMessageStore();
  const isLike = templateProps?.likeOrDislike === "like";
  const reply = t.aiFeedback.thanksAIFeedback;
  const { addToReportQueue, requireReportProps } = useReportRequestQueue();
  const reasonOptions = [
    { value: 10, label: t.aiFeedback["10"] },
    { value: 30, label: t.aiFeedback["30"] },
    { value: 40, label: t.aiFeedback["40"] },
    { value: 50, label: t.aiFeedback["50"] },
    { value: 1000, label: t.aiFeedback["1000"] },
  ];
  const [spinning, setSpinning] = useState(false);
  const [feedbackType, setFeedbackType] = useState<number>();
  const [submit, setSubmit] = useState(false);
  const [feedbackContent, setFeedbackContent] = useState<string>("");
  const isDislike = templateProps?.likeOrDislike === "dislike";

  useEffect(() => {
    const params = {
      languageCode: _lng,
      type: IMConfigTypeEnum.feedback,
    };
    queryFeedbackConfig(params)
      .then((res) => {
        setSpinning(false);
        const { content = "", content2 = "" } = res || {};
        const _content = isLike ? content : content2;
        setFeedbackContent(_content || reply);
      })
      .finally(() => setSpinning(false));
  }, []);

  const handleSubmit = (values: Record<string, unknown>) => {
    if (submit) return;
    console.log("表单提交:", values);
    const feedback = values["feedback"] as string;
    setSubmit(true);
    const item = reasonOptions.find((item) => item.value === feedbackType);

    const content = item?.label || "";
    addToReportQueue({
      bizType: ReportBizTypeEnum.AI_CHAT,
      createTime: new Date().getTime(),
      scene: ReportSceneTypeEnum.AI_CONSULT,
      eventType: isLike
        ? ReportEventEnum.SUBMIT_LIKE_ADVICE
        : ReportEventEnum.SUBMIT_DISLIKE_ADVICE,
      extendField1: feedbackType,
      extendField2: feedbackType
        ? +feedbackType === 1000 || isLike
          ? feedback
          : content
        : feedback,
      parentContent: parentContentInfo?.parentContent,
      parentContentId: parentContentInfo?.parentContentId,
      parentContentType: parentContentInfo?.parentContentType,
    });
    const sessionId = createSessionId();
    const params: FeedbackPropsType = {
      userId: user?.cusId || sessionId,
      feedbackClass:
        templateProps?.likeOrDislike === "like"
          ? FeedbackTypeEnum.like
          : FeedbackTypeEnum.dislike,
      feedbackType,
      feedback,
      knowledgeId: AIAnswerKnowledgeId,
      sessionId,
      origin: isApp() ? OriginType.app : OriginType.web,
      requestId: templateProps?.requestId || "",
    };
    questionFeedback(params).then(() => {
      // 提交之后，去拿回复模板
      queryFeedbackConfig({
        languageCode: _lng,
        type: configType,
      }).then((res) => {
        setSpinning(false);
        const { content = "", content2 = "" } = res || {};
        // 如果接口返回为空，使用默认的 thanksAIFeedback 话语
        const _content = content || content2 || reply;
        setRobotMessage({
          clientId: createClientUUid(MessageType.Text),
          content: _content,
          timestamp: Date.now(),
          streaming: true,
          type: MessageType.Text,
          headerType: HeaderType.MESSAGE,
          senderType: MessageSender.ROBOT,
          senderName: "智能助手",
          messageType: RobotMessageType.ROBOT_NORMAL_REPLY,
          robotData: {
            parentContentInfo,
          },
        });
      });
    });
  };

  return spinning ? (
    <Spin />
  ) : (
    <div className={styles.collectOrderInfo}>
      <div className={styles.titleWrapper}>
        <div
          className={styles.title}
          onClick={() => {}}
          dangerouslySetInnerHTML={{
            __html: feedbackContent,
          }}
        ></div>
      </div>
      <Form className={styles.form} layout="vertical" onSubmit={handleSubmit}>
        {isDislike && (
          <Form.RadioGroup
            field="feedbackType"
            label={t.aiFeedback.feedbackTypeLabel}
            rules={[{ required: true, message: "请选择原因" }]}
            disabled={submit}
            onChange={(e) => setFeedbackType(e.target.value)}
          >
            <Space vertical align="start">
              {reasonOptions.map((item, index) => (
                <Radio key={index} value={item.value}>
                  {item.label}
                </Radio>
              ))}
            </Space>
          </Form.RadioGroup>
        )}
        <Form.TextArea
          field="feedback"
          label={t.aiFeedback.feedbackLabel}
          rules={[
            {
              required: isDislike ? feedbackType === 1000 : true,
              message: "请填写反馈内容",
            },
          ]}
          className={styles.feedbackInput}
          rows={4}
          placeholder={t.aiFeedback.feedbackPlaceholder}
          maxLength={500}
          disabled={submit}
        />
        <div className={styles.btnWrapper}>
          <Button
            theme="solid"
            type="primary"
            htmlType="submit"
            disabled={submit}
          >
            {t.aiFeedback.submit}
          </Button>
        </div>
      </Form>
    </div>
  );
}
